import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import {
  scheduledScansApi,
  type CreateScheduledScanRequest,
  type UpdateScheduledScanRequest,
} from '../lib/api/scheduledScans';
import { log } from '@/lib/logger';

log.info('[useScheduledScans] Module loaded');

const SCHEDULED_SCANS_KEY = ['scheduled-scans'];

export function useScheduledScans() {
  log.info('[useScheduledScans] useScheduledScans called');
  return useApiQuery({
    queryKey: SCHEDULED_SCANS_KEY,
    queryFn: (token) => scheduledScansApi.list(token),
    // Next-run times and last-scan state advance server-side via the hourly
    // cron while the dashboard sits idle. The app-default staleTime would
    // serve a stale "next run" on a returning visit until some mutation
    // invalidated the key (same reasoning as useClientReviews).
    staleTime: 0,
  });
}

export function useCreateScheduledScan() {
  log.info('[useScheduledScans] useCreateScheduledScan called');
  return useApiMutation<
    Awaited<ReturnType<typeof scheduledScansApi.create>>,
    CreateScheduledScanRequest
  >({
    mutationFn: (token, data) => scheduledScansApi.create(token, data),
    invalidateKeys: [SCHEDULED_SCANS_KEY],
  });
}

export function useUpdateScheduledScan() {
  log.info('[useScheduledScans] useUpdateScheduledScan called');
  return useApiMutation<
    Awaited<ReturnType<typeof scheduledScansApi.update>>,
    { id: string; data: UpdateScheduledScanRequest }
  >({
    mutationFn: (token, { id, data }) => scheduledScansApi.update(token, id, data),
    invalidateKeys: [SCHEDULED_SCANS_KEY],
  });
}

/**
 * Run one schedule on demand.
 *
 * Invalidates the list like every other mutation: a run rewrites
 * lastScannedAt, the score, the delta counts and the report link, so the row
 * the user is looking at is stale the moment it resolves.
 */
export function useRunScheduledScan() {
  log.info('[useScheduledScans] useRunScheduledScan called');
  const queryClient = useQueryClient();
  return useApiMutation<Awaited<ReturnType<typeof scheduledScansApi.run>>, string>({
    mutationFn: (token, id) => scheduledScansApi.run(token, id),
    invalidateKeys: [SCHEDULED_SCANS_KEY],
    // A refused run can persist a pause before returning 400 (server
    // services/rescan/scanRun.ts recordScanFailure). A lost response can also
    // follow a completed write. Re-read the row in both cases.
    onError: () => { void queryClient.invalidateQueries({ queryKey: SCHEDULED_SCANS_KEY }); },
  });
}

/**
 * Open a team review on a schedule's stored canvas.
 *
 * NO invalidateKeys, unlike its siblings. This creates a review; it changes
 * nothing about the schedule row, so refetching the list would be a request
 * that can only return what is already on screen.
 */
export function useReviewScheduledScanFindings() {
  log.info('[useScheduledScans] useReviewScheduledScanFindings called');
  return useApiMutation<Awaited<ReturnType<typeof scheduledScansApi.reviewFindings>>, string>({
    mutationFn: (token, id) => scheduledScansApi.reviewFindings(token, id),
  });
}

export function useDeleteScheduledScan() {
  log.info('[useScheduledScans] useDeleteScheduledScan called');
  return useApiMutation<
    Awaited<ReturnType<typeof scheduledScansApi.delete>>,
    string
  >({
    mutationFn: (token, id) => scheduledScansApi.delete(token, id),
    invalidateKeys: [SCHEDULED_SCANS_KEY],
  });
}
